'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { useModal } from '@/components/context/modal-context';

import Typography from '@/components/primitives/typography';
import Button from '@/components/primitives/button';
import styles from './styles.module.scss';

const CLOSED_AT_STORAGE_KEY = 'app-launch-modal-closed-at';
const SHOWN_STORAGE_KEY = 'app-launch-modal-shown';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const OPEN_DELAY_MS = 4000;

const AppLaunchModalContent = ({ onDismiss }: { onDismiss: () => void }) => {
  const { closeModal } = useModal();

  const handleCloseModal = () => {
    onDismiss();
    closeModal();
  };

  const handleLaunchApp = useCallback(() => {
    onDismiss();

    // 앱 실행을 위한 커스텀 스킴 URL 구성
    const { pathname, search, hash } = window.location;
    const scheme = process.env.NEXT_PUBLIC_APP_SCHEME || 'fm-lone';
    const appSchemeUrl = `${scheme}://${pathname.startsWith('/') ? pathname.slice(1) : pathname}${search}${hash}`;
    
    // 앱 미설치 시 스토어로 이동하기 위한 URL 설정
    const userAgent = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    
    // TODO: 실제 서비스용 앱 스토어 ID가 확정되면 환경 변수 등으로 관리 필요
    const APP_STORE_URL = 'https://apps.apple.com/app/id6757098843';
    const PLAY_STORE_URL = 'market://details?id=com.followingmoments.lone';
    const PLAY_STORE_WEB_URL = 'https://play.google.com/store/apps/details?id=com.followingmoments.lone';

    const startTime = Date.now();
    
    // 앱 실행 시도 (커스텀 스킴 사용)
    window.location.href = appSchemeUrl;
    
    // 일정 시간 내에 페이지에 남아있다면 앱이 설치되지 않은 것으로 간주하고 스토어로 이동.
    setTimeout(() => {
      // 2.5초 내에 다시 체크 (앱이 실행되었다면 이 타이머가 실행되지 않거나 실행 시점이 늦어짐)
      if (Date.now() - startTime < 3000) {
        if (isIOS) {
          window.location.href = APP_STORE_URL;
        } else if (isAndroid) {
          // Android는 market:// 스킴 시도 후 실패 시 웹 URL 시도
          window.location.href = PLAY_STORE_URL;
          setTimeout(() => {
            window.location.href = PLAY_STORE_WEB_URL;
          }, 500);
        }
      }
    }, 2500);
    
    // 약간의 지연 후 모달 닫기
    setTimeout(() => {
      closeModal();
    }, 500);
  }, [closeModal, onDismiss]);

  return (
    <div className={styles.container}>
      <Typography
        size="medium"
        weight="regular"
        align="center"
        className={styles.description}
      >
        LoNe 앱으로 이동하여{'\n'}더욱 편리하게 이용해보세요!
      </Typography>
      <div className={styles.buttonGroup}>
        <Button
          variant="secondary"
          onClick={handleCloseModal}
          className={styles.button}
        >
          나중에 하기
        </Button>
        <Button
          variant="primary"
          onClick={handleLaunchApp}
          className={styles.button}
        >
          앱으로 열기
        </Button>
      </div>
    </div>
  );
};

export const AppLaunchModalTrigger = () => {
  const { isOpen, openModal } = useModal();
  const appModalOpenedRef = useRef(false);

  const recordDismissal = useCallback(() => {
    try {
      localStorage.setItem(CLOSED_AT_STORAGE_KEY, Date.now().toString());
    } catch {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }, []);

  useEffect(() => {
    if (appModalOpenedRef.current && !isOpen) {
      recordDismissal();
      appModalOpenedRef.current = false;
    }
  }, [isOpen, recordDismissal]);

  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    if (!isMobile) {
      return;
    }

    try {
      if (sessionStorage.getItem(SHOWN_STORAGE_KEY)) {
        return;
      }

      const closedAt = Number(localStorage.getItem(CLOSED_AT_STORAGE_KEY));
      if (closedAt && Date.now() - closedAt < DISMISS_DURATION_MS) {
        return;
      }
    } catch {
      // Fall back to showing the modal when browser storage is unavailable.
    }

    const openTimer = window.setTimeout(() => {
      try {
        sessionStorage.setItem(SHOWN_STORAGE_KEY, 'true');
      } catch {
        // Fall back to showing the modal on subsequent pages when storage is unavailable.
      }

      appModalOpenedRef.current = true;

      openModal({
        title: 'LoNe 앱 설치 안내',
        content: <AppLaunchModalContent onDismiss={recordDismissal} />,
      });
    }, OPEN_DELAY_MS);

    return () => {
      window.clearTimeout(openTimer);
    };
  }, [openModal, recordDismissal]);

  return null;
};
